/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CommerceConf,
  CommerceConfDoc,
  DeliveryTaskInfo,
  LogDoc,
  LogOrder,
  Order,
  OrderDoc,
  OrderProduct,
  OrderStatusCode,
  PickupTaskInfo,
  ProductDoc,
  ProductStateCode,
  StoreAddressBase,
  StoreDoc,
  UserDoc,
} from '@gooduncles/gu-app-schema';
import { message } from 'antd';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { groupBy } from 'lodash';
import { differenceWith, isEqual } from 'lodash-es';
import { GaEventType } from 'src/schema/schema-ga-event';
import { MobileAlert, errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import {
  cancelOrder,
  firebaseTransaction,
  getCommerceConf,
  getCommerceConfDocRef,
  getOrderDocRef,
  getOrderedOrderCountForStore,
  getProductDoc,
  getProductDocRef,
  getSubmittedOrderCountForStore,
  sendGaEvent,
} from 'src/utils/firebase/firebase-short-cut';
import { errorLogger, logger } from 'src/utils/logger';
import { invalidateCartProductCaches } from 'src/utils/swr-cache-util';

import { CartState } from 'src/redux/slices/cart';

/**
 * 장바구니에 상품을 최초 담는 과정에서 수량을 조정하는 기능을 제공하지 않고있기 때문에
 * 기본 수량을 1로 둔다.
 */
export const productToOrderProduct = (product: ProductDoc, volume: number): OrderProduct => {
  const {
    name,
    imageUrl,
    thumbnail,
    imageDescription,
    badge,
    memo,
    dailyNotification,
    costUpdatedAt,
    salesAmount,
    _timeCreate,
    _timeDelete,
    _timeUpdate,
    ...productBase
  } = product;

  return {
    ...productBase,
    productId: product._id,
    volume,
    price: product.price,
    snapshotId: null,
    snapshotPrice: null,
    priceDiff: null,
  };
};

/**
 * 주문 데이터를 생성한다.
 */
export const createOrderData = (
  user: Omit<UserDoc, 'favoriteProducts'>,
  store: StoreDoc,
  cart: CartState,
  deliveryFee: number,
  deliveryDateRules: number[]
): Order => {
  const now = new Date();
  const orderDate = formatDate(now, "yyyy-MM-dd'T'HH:mm:ss+0900");
  const deliveryDate = getDeliveryDate(deliveryDateRules, now);

  const storeAddress: StoreAddressBase = {
    address: store.address ?? '',
    roadAddress: store.roadAddress ?? '',
    jibunAddress: store.jibunAddress ?? '',
    sido: store.sido ?? '',
    sigungu: store.sigungu ?? '',
    bname: store.bname ?? '',
    hname: store.hname ?? '',
    roadname: store.roadname ?? '',
  };

  const invoiceDetails = {
    settledAt: null,
    invoiceId: null,
  };

  const deliveryDetails: DeliveryTaskInfo = {
    deliveryPartnerId: null,
    deliveryPartnerName: null,
    deliverySpotId: null,
    deliveryTaskFinishedAt: null,
  };

  const pickupDetails: PickupTaskInfo = {
    pickupPartnerId: null,
    pickupPartnerName: null,
    pickupTaskFinishedAt: null,
  };
  const grandTotal = cart.totalAmount + deliveryFee;

  return {
    date: null,
    orderDate,
    products: cart.products,
    orderStatus: OrderStatusCode.SUBMITTED,
    totalAmount: cart.totalAmount,
    totalAmountDiff: null,
    paidAmount: grandTotal,
    grandTotal,
    userId: user._id,
    storeId: user.storeId,
    deliveryFee,
    storeAddress,
    deliveryDate,
    ...invoiceDetails,
    ...deliveryDetails,
    ...pickupDetails,
  };
};

const splitOrderableProducts = (products: (ProductDoc | undefined)[]) => {
  const groupByState = groupBy(products, 'state');
  const stockProducts = (groupByState[ProductStateCode.STOCK] ?? []).filter((product) => product) as ProductDoc[];
  const outOfStockProducts = groupByState[ProductStateCode.OUTOFSTOCK] ?? [];
  const discontinuedProducts = groupByState[ProductStateCode.DISCONTINUED] ?? [];
  return {
    orderable: stockProducts,
    // 품절 또는 판매중지 상태의 상품을 걸러낸다.
    unorderable: [...outOfStockProducts, ...discontinuedProducts],
  };
};

/**
 * 불러오는 이전 주문 상품들이 유효한지 확인합니다.
 */
export const loadPrevOrderProducts = async (products: OrderProduct[]) => {
  const currentProducts = await Promise.all(products.map((product) => getProductDoc(product.productId)));
  const filteredList = splitOrderableProducts(currentProducts);

  if (filteredList.orderable.length === 0) {
    message.warning('더 이상 판매하지 않는 상품들입니다.');
    return false;
  }

  if (filteredList.orderable.length !== products.length) {
    message.warning(`구매할 수 없는 상품(${filteredList.unorderable.length}개)을 제외하고 장바구니에 담았습니다.`);
  }
  return filteredList.orderable
    .map((product) => {
      const orderProduct = products.find((orderProduct) => orderProduct.productId === product.productId);
      if (!orderProduct) {
        return undefined;
      }
      const volume = orderProduct.volume < 1 ? 1 : Math.floor(orderProduct.volume);
      const newOrderProduct = productToOrderProduct(product, volume);
      return newOrderProduct;
    })
    .filter((product) => product) as OrderProduct[];
};

/**
 * 배송비를 계산합니다.
 */
export const getDeliveryFee = (store: StoreDoc | null, conf: CommerceConf, totalAmount: number) => {
  if (store?.chargeDeliveryFee && totalAmount < conf.minAmountForFreeDelivery) {
    return conf.deliveryFee;
  }
  return 0;
};

/**
 * 최초 주문 여부를 판별한다.
 * 주문을 방해하지 않기 위해서 비동기로 처리한다.
 */
export const checkIsFirstOrder = async (store: StoreDoc & { timeCreate: string }) => {
  try {
    if (!store?._id) {
      return;
    }
    const count = await getOrderedOrderCountForStore(store._id);
    if (count === 0) {
      logger.logCommerce(
        `🎉 첫 주문 🎉\n매장명: ${store.storeNickname}\n코드: ${store.storeCode}\n가입날짜: ${store.timeCreate}`,
        {
          channel: '4-1-앱-알림-신규',
        }
      );
    }
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    logger.logCommerce(`첫 주문 확인 과정에서 오류가 발생했습니다.\nerror: ${msg}`, {
      level: 'error',
    });
  }
};

/** 휴면 고객 기준 일수 */
const DORMANT_DAYS_THRESHOLD = 50;

/**
 * 휴면 고객의 재주문 여부를 판별한다.
 * 주문을 방해하지 않기 위해서 비동기로 처리한다.
 */
export const checkIsDormantCustomerReorder = async (store: StoreDoc & { timeCreate: string }) => {
  try {
    if (!store?._id || !store.latestOrderAt) {
      return;
    }

    const latestOrderDate = new Date(store.latestOrderAt);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - latestOrderDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays >= DORMANT_DAYS_THRESHOLD) {
      logger.logCommerce(
        `🔄 휴면 고객 재주문 🔄\n매장명: ${store.storeNickname}\n코드: ${store.storeCode}\n마지막 주문일: ${store.latestOrderAt}\n휴면 기간: ${diffDays}일`,
        {
          channel: '4-1-앱-알림-신규',
        }
      );
    }
  } catch (error) {
    console.error(error);
    const msg = errorObjectToString(error);
    logger.logCommerce(`휴면 고객 재주문 확인 과정에서 오류가 발생했습니다.\nerror: ${msg}`, {
      level: 'error',
    });
  }
};

export const calcTotalAmount = (products: OrderProduct[]) =>
  products.reduce((acc, product) => {
    return acc + product.volume * product.price;
  }, 0);

/** 단가 */
export const getValueOfSupply = (price: number) => Math.round(price / 1.1);
/** 부가세 */
export const getValueAddedTax = (price: number) => price - Math.round(price / 1.1);

/**
 * 필요한 세금 항목을 계산한다.
 * 세금은 총 금액에 대한 10%로 계산한다.
 *
 * @param price
 * @param volume
 * @param taxFree
 * @returns {
 *  supplyPriceForUnit: 상품의 개별 공급가,
 *  volume: 수량,
 *  totalPrice: 세금과 수량을 계산한 총 금액,
 *  tax: 총 부가세
 * }
 */
export const calcTaxForPrice = (price: number, volume: number, taxFree: boolean) => {
  // 면세항목인 경우 세금계산을 별도로 하지 않는다.
  if (taxFree) {
    return {
      supplyPrice: price,
      volume,
      totalPrice: price * volume,
      tax: 0,
    };
  }
  const supplyPrice = getValueOfSupply(price);
  const totalPrice = price * volume;
  const tax = getValueAddedTax(totalPrice);

  return {
    supplyPrice,
    volume,
    totalPrice,
    tax,
  };
};

/**
 * logOrder는 주문의 변동사항이 있는 경우 그 변경 사항의 기록이다.
 * before와 after를 통해 변경 전과 변경 후의 주문을 확인할 수 있다.
 * 오리지널 주문의 상품 목록을 가져와서 변동 사항이 있는 경우 그 내용을 표시한다.
 */
export const getOrderProductsDiffs = (products?: OrderProduct[], logOrders?: LogDoc<LogOrder>[]) => {
  if (!products) return;
  if (!logOrders || logOrders.length === 0) return products;

  const diffProductList = logOrders
    .sort((a, b) => a._timeCreate.toDate().getTime() - b._timeCreate.toDate().getTime())
    .map((log) => {
      const { before, after } = log.body;
      if (!before.products || !after.products) return;
      const diffProducts = differenceWith(before.products, after.products, isEqual);
      const diffProductIds = diffProducts.map((p) => p.productId);

      if (!diffProducts) return;

      const diffLog = diffProductIds
        .map((productId) => {
          const beforeProduct = before.products?.find((p) => p.productId === productId);
          const afterProduct = after.products?.find((p) => p.productId === productId);
          if (!beforeProduct || !afterProduct) return;
          return [productId, `${beforeProduct.volume} → ${afterProduct.volume > 0 ? afterProduct.volume : '품절'}`];
        })
        .filter((p) => p) as [string, string][]; // [productId, changeLog]

      return diffLog;
    })
    .flat()
    .filter((p) => p) as [string, string][]; // [productId, changeLog]

  const diffProductMap = diffProductList.reduce((acc, [productId, changeLog]) => {
    acc[productId] = acc[productId] || [];
    acc[productId].push(changeLog);
    return acc;
  }, {} as Record<string, string[]>);

  return products.map((product) => {
    const changeLogs = diffProductMap[product.productId];
    return changeLogs?.length ? { ...product, _ui: { changeLog: changeLogs.join(', ') } } : product;
  });
};

/**
 * @param days
 * @param orderDate
 * @returns
 */
export const getDeliveryDate = (currentRole: number[], orderDate?: Date) => {
  const now = orderDate ?? new Date();
  const weekday = new Date(now).getDay();
  const days = currentRole[weekday];
  return format(new Date(now.setDate(now.getDate() + days)), 'yyyy-MM-dd');
};

/**
 * 주문생성
 */
export const handleCreateOrder = async (orderData: Order, storeName: string) => {
  const isOrderable = await checkIsOrderable(orderData.storeId);
  if (!isOrderable) {
    throw new Error('이미 생성한 주문이 있습니다.');
  }

  const orderDocRef = getOrderDocRef();

  try {
    await firebaseTransaction(async (transaction) => {
      const commerceConfigRef = getCommerceConfDocRef();
      const commerceConfigSnapshot = await transaction.get(commerceConfigRef);
      const commerceConfig = commerceConfigSnapshot.data() as CommerceConfDoc;
      if (!commerceConfig.open) {
        throw new Error('주문이 불가능한 시간입니다.');
      }
      if (commerceConfig.closed.isClosed) {
        throw new Error(commerceConfig.closed.msgClosed);
      }

      // 상품수량 및 상태체크
      const promises = orderData.products.map((p) => transaction.get(getProductDocRef(p.productId)));
      const products = await Promise.all(promises);
      const soldOutProducts = products.filter((p) => p.data()?.state !== ProductStateCode.STOCK);
      if (soldOutProducts.length > 0) {
        // 품절된 상품들의 캐시를 무효화하여 최신 상태 반영
        const soldOutProductIds = soldOutProducts.map((p) => p.id);
        invalidateCartProductCaches(soldOutProductIds);

        throw new Error(`구매 불가능한 상품이 있습니다. ${soldOutProducts.map((p) => p.data()?.fullName).join(', ')}`);
      }

      transaction.set(orderDocRef, {
        ...orderData,
        _id: orderDocRef.id,
        _timeCreate: Timestamp.now(),
      });
    });

    sendGaEvent({
      name: GaEventType.ORDER_SUBMIT,
      eventParams: {
        ...orderData,
        orderId: orderDocRef.id,
      },
    });
    logger.logCommerce(`주문완료 - 업소명: ${storeName}, 주문금액: ${orderData.grandTotal}`, {
      extraInfo: {
        orderId: orderDocRef.id,
      },
    });
  } catch (error) {
    // 주문 생성 실패 시 장바구니 상품들의 캐시 무효화 (상태 변경 가능성)
    const productIds = orderData.products.map((p) => p.productId);
    invalidateCartProductCaches(productIds);

    const errorMessage = errorObjectToString(error);
    MobileAlert('주문생성 실패', errorMessage ?? '주문을 생성할 수 없습니다. 다시 시작후 시도해주세요.');
    errorLogger(`주문생성 실패 - 업소명: ${storeName}, 주문번호: ${orderDocRef.id}\nerror: ${errorMessage}`);

    // 예외를 다시 throw하여 호출하는 쪽에서 실패를 감지할 수 있도록 함
    throw error;
  }
};

/**
 * 주문수정
 */
export const handleUpdateOrder = async (orderId: string, storeName: string, orderData: Order) => {
  logger.logCommerce(`주문수정 - 업소명: ${storeName}, 주문금액: ${orderData.grandTotal}`, {
    extraInfo: {
      orderId,
    },
  });

  try {
    await firebaseTransaction(async (transaction) => {
      // 주문 가능 시간 체크
      const commerceConfigRef = getCommerceConfDocRef();
      const commerceConfigSnapshot = await transaction.get(commerceConfigRef);
      const commerceConfig = commerceConfigSnapshot.data() as CommerceConfDoc;
      if (!commerceConfig.open) {
        throw new Error('주문이 불가능한 시간입니다.');
      }
      if (commerceConfig.closed.isClosed) {
        throw new Error(commerceConfig.closed.msgClosed);
      }

      // 상품수량 및 상태체크
      const promises = orderData.products.map((p) => transaction.get(getProductDocRef(p.productId)));
      const products = await Promise.all(promises);
      const soldOutProducts = products.filter((p) => p.data()?.state !== ProductStateCode.STOCK);
      if (soldOutProducts.length > 0) {
        // 품절된 상품들의 캐시를 무효화하여 최신 상태 반영
        const soldOutProductIds = soldOutProducts.map((p) => p.id);
        invalidateCartProductCaches(soldOutProductIds);

        throw new Error(`구매 불가능한 상품이 있습니다. ${soldOutProducts.map((p) => p.data()?.fullName).join(', ')}`);
      }

      const orderDocRef = getOrderDocRef(orderId);
      transaction.update(orderDocRef, {
        ...orderData,
        _timeUpdate: Timestamp.now(),
      });
    });

    sendGaEvent({
      name: GaEventType.ORDER_EDIT_SUBMIT,
      eventParams: {
        orderId,
      },
    });
  } catch (error: any) {
    // 주문 수정 실패 시 모든 상품의 캐시 무효화 (상태 변경 가능성)
    const productIds = orderData.products.map((p) => p.productId);
    invalidateCartProductCaches(productIds);

    const errorMessage = errorObjectToString(error);
    MobileAlert('주문수정 실패', '주문을 변경할 수 없습니다. 다시 시작후 시도해주세요.');
    errorLogger(`주문수정 실패 - 업소명: ${storeName}, 주문번호: ${orderId}\nerror: ${errorMessage}`);

    // 예외를 다시 throw하여 호출하는 쪽에서 실패를 감지할 수 있도록 함
    throw error;
  }
};

/**
 * 주문취소
 */
export const onCancelOrder = async (order: OrderDoc, storeName: string, options?: { resetCart?: boolean }) => {
  logger.logCommerce(`주문취소 - 업소명: ${storeName}, 취소금액: ${order.grandTotal}`, {
    extraInfo: {
      orderId: order._id,
    },
  });

  try {
    await cancelOrder(order._id);
    sendGaEvent({
      name: GaEventType.ORDER_CANCEL,
      eventParams: {
        orderId: order._id,
      },
    });
    MobileAlert('주문취소 완료', options?.resetCart ? '주문을 취소했습니다.' : '취소된 상품을 장바구니로 옮겼습니다.');
  } catch (error: any) {
    const errorMessage = errorObjectToString(error);
    MobileAlert('주문취소 실패', '주문을 취소할 수 없습니다.');
    errorLogger(`주문취소 실패 - 업소명: ${storeName}, 주문번호: ${order._id}\nerror: ${errorMessage}`);

    // 예외를 다시 throw하여 호출하는 쪽에서 실패를 감지할 수 있도록 함
    throw error;
  }
};

/**
 * 실제 주문을 하기전에 이미 주문한 주문이 있는지 확인한다.
 * 있다면 주문을 할 수 없으므로 false를 리턴한다.
 */
export const checkIsOrderable = async (storeId: string) => {
  const submittedOrderCount = await getSubmittedOrderCountForStore(storeId);
  return submittedOrderCount < 1;
};

interface OrderError {
  title: string;
  content?: string;
  customAlert?: 'kakaoLink';
}

type ValidateOrderConditionsResult =
  | {
      isValid: true;
      user: Omit<UserDoc, 'favoriteProducts'>;
      store: StoreDoc & { timeCreate: string };
      commerceConf: CommerceConf;
    }
  | {
      isValid: false;
      error: OrderError;
    };

/**
 * 주문 가능 조건을 확인한다.
 */
export const validateOrderConditions = async (
  user: Omit<UserDoc, 'favoriteProducts'> | null,
  store: (StoreDoc & { timeCreate: string }) | null
): Promise<ValidateOrderConditionsResult> => {
  const log = (msg: string) => logger.logCommerce(msg, { channel: '4-앱-알림' });
  // 1. 사용자 정보 체크
  if (user === null) {
    log(`🚨 ${store?.storeNickname} 사용자 정보가 없는 상태로 주문을 시도했습니다.`);
    return {
      isValid: false,
      error: {
        title: '사용자 정보가 없습니다.',
      },
    };
  }

  // 2. 매장 정보 체크
  if (store === null) {
    log(`🚨 ${user._id} 매장 정보가 없는 상태로 주문을 시도했습니다.`);
    return {
      isValid: false,
      error: {
        title: '매장 정보가 없습니다.',
      },
    };
  }
  if (store.state === 'break') {
    log(`🔴 휴업 처리된 매장(${store.storeNickname})이 주문을 시도했습니다.`);
    return {
      isValid: false,
      error: {
        title: '주문 접수 중지된 매장입니다.',
        content: '주문 접수를 원하시는 경우\n카카오톡 채널(이웃삼촌)으로 문의 바랍니다.',
      },
    };
  }
  if (store.state === 'closed') {
    log(`🔴 폐업 처리된 매장(${store.storeNickname})이 주문을 시도했습니다.`);
    return {
      isValid: false,
      error: {
        title: '운영 중지된 매장입니다.',
        content: '운영 중지 해제를 원하시는 경우\n카카오톡 채널(이웃삼촌)으로 문의 바랍니다.',
      },
    };
  }
  if (store.state === 'guest') {
    log(`🟡 승인 대기중인 매장(${store.storeNickname})이 주문을 시도했습니다.`);
    return {
      isValid: false,
      error: {
        title: '승인 대기중인 매장입니다.',
        customAlert: 'kakaoLink',
      },
    };
  }

  // 3. 주문 가능 시간 체크
  const currentCommerceConf = await getCommerceConf(true);
  if (currentCommerceConf === undefined || !currentCommerceConf.open) {
    log(`🚨 ${store.storeNickname} 매장이 주문 불가 시간에 주문을 시도했습니다.`);
    return {
      isValid: false,
      error: {
        title: '주문 가능한 시간이 아닙니다.',
      },
    };
  }
  return {
    isValid: true,
    user,
    store,
    commerceConf: currentCommerceConf,
  };
};

/**
 * 주문을 생성하는 경우 장바구니에 상품이 존재하는지 확인한다.
 * @param cart 장바구니
 * @returns 장바구니에 상품이 존재하지 않는 경우 오류 메시지를 반환한다.
 */
export const validateOrderCreatable = (cart: CartState): OrderError | undefined => {
  if (cart.products.length === 0) {
    return {
      title: '상품이 없습니다.',
    };
  }
  return undefined;
};

type ValidateOrderEditableResult =
  | {
      isValid: true;
      order: OrderDoc;
    }
  | {
      isValid: false;
      error: OrderError;
    };

/**
 * 주문을 수정하는 경우 주문이 존재하는지 확인한다.
 * @param order 수정하려는 주문
 * @returns 주문이 존재하지 않는 경우 오류 메시지를 반환한다.
 */
export const validateOrderEditable = async (
  order: OrderDoc | null,
  store: StoreDoc
): Promise<ValidateOrderEditableResult> => {
  if (order === null) {
    errorLogger(`🚨 ${store.storeNickname} 매장에서 더 이상 존재하지 않는 주문을 수정하려고 했습니다.`);
    return {
      isValid: false,
      error: {
        title: '더 이상 존재하지 않는 주문입니다.',
      },
    };
  }
  return {
    isValid: true,
    order,
  };
};
